import React, { useMemo, useState } from 'react';
import type { WorkflowComponent, WorkflowMalformedComponent } from '../../../types/workflow';
import { cardRefOf, type EditableWorkflow } from '../../../utils/workflowDsl';
import { Banner } from './WorkflowFormControls';
import { DefBlocks, DefValue, definitionSignals, isPlainObject, type DefSignal } from './WorkflowDefinitionView';

/**
 * Full-page card views for the snapshot's library sections (Library / Triggers /
 * Strategies / Ontology), shown via the Workflow sub-navigation instead of the
 * side panel. Definitions are rendered as structured content by
 * `WorkflowDefinitionView`; nested values remain structured at every depth.
 */

// ─── Kind metadata ───────────────────────────────────────────────────────────

export const KIND_META: Record<string, { title: string; one: string; icon: string; color: string }> = {
  communicative_unit: { title: 'Communicative units', one: 'communicative unit', icon: 'fa-comment-dots', color: '#0099FA' },
  strategy: { title: 'Strategies', one: 'strategy', icon: 'fa-chess-knight', color: '#FF8300' },
  trigger: { title: 'Triggers', one: 'trigger', icon: 'fa-bolt', color: '#0099FA' },
  response_class: { title: 'Response classes', one: 'response class', icon: 'fa-reply', color: '#FF8300' },
  stage_policy: { title: 'Stage policies', one: 'stage policy', icon: 'fa-layer-group', color: '#0099FA' },
  client_style: { title: 'Client styles', one: 'client style', icon: 'fa-user-tag', color: '#FF8300' },
};

const kindMeta = (kind: string) => KIND_META[kind] || { title: kind, one: kind, icon: 'fa-cube', color: '#5B7893' };

// ─── One component card ──────────────────────────────────────────────────────

export interface CardUsage {
  label: string;
  onClick: () => void;
}

const SignalRow: React.FC<{ signals: DefSignal[]; usages?: CardUsage[] | null }> = ({ signals, usages }) => {
  if (!signals.length && !usages?.length) return null;
  return (
    <div className="cwf-libcard__signals">
      {signals.map((s) => (
        <span key={s.key} className="cwf-sig" title={s.key}>
          <span className="cwf-sig__k">{s.label}</span>
          <span className="cwf-sig__v">{s.value}</span>
        </span>
      ))}
      {usages?.length ? (
        <span className="cwf-sig cwf-sig--use">
          <i className="fas fa-link" aria-hidden="true" />
          used by {usages.length}
        </span>
      ) : null}
    </div>
  );
};

const ComponentCard: React.FC<{
  component: WorkflowComponent;
  usages?: CardUsage[] | null;
}> = ({ component: c, usages }) => {
  const [open, setOpen] = useState(false);
  const meta = kindMeta(c.kind);
  const description = typeof c.definition?.description === 'string' ? c.definition.description : null;
  const signals = useMemo(() => definitionSignals(c.definition, ['description']), [c.definition]);
  const fieldCount = isPlainObject(c.definition) ? Object.keys(c.definition).filter((k) => k !== 'description').length : 0;

  return (
    <article
      className={`cwf-libcard ${c.archived_at ? 'cwf-libcard--archived' : ''} ${open ? 'cwf-libcard--open' : ''}`}
      style={{ ['--card-accent' as string]: meta.color }}
    >
      <button type="button" className="cwf-libcard__head" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className="cwf-libcard__icon" aria-hidden="true">
          <i className={`fas ${meta.icon}`} />
        </span>
        <span className="cwf-libcard__id">
          <span className="cwf-libcard__name">{c.name}</span>
          <span className="cwf-libcard__meta">
            <span className="cwf-libcard__kind">{meta.one}</span>
            <span className="cwf-libcard__ver">v{c.version}</span>
            {c.archived_at ? <span className="cwf-libcard__flag">archived</span> : null}
          </span>
        </span>
        <i className={`fas ${open ? 'fa-chevron-up' : 'fa-chevron-down'} cwf-libcard__chevron`} aria-hidden="true" />
      </button>

      {description ? <p className={`cwf-libcard__desc ${open ? '' : 'cwf-libcard__desc--clamp'}`}>{description}</p> : null}

      {/* The open body states these facts in full — repeating them above it is just noise. */}
      {open ? null : <SignalRow signals={signals} usages={usages} />}

      {open ? (
        <div className="cwf-libcard__body">
          {fieldCount ? (
            <DefBlocks obj={c.definition} skip={['description']} />
          ) : !description ? (
            <span className="cwf-def__muted">The definition is empty.</span>
          ) : null}

          {usages ? (
            <section className="cwf-defblock" data-testid="cwf-usedby">
              <header className="cwf-defblock__head">
                <h4 className="cwf-defblock__title">Used by</h4>
                <span className="cwf-defblock__count">{usages.length}</span>
              </header>
              {usages.length ? (
                <span className="cwf-chips cwf-chips--tight">
                  {usages.map((u, i) => (
                    <button key={i} type="button" className="cwf-chip cwf-chip--link" onClick={u.onClick}>
                      {u.label}
                    </button>
                  ))}
                </span>
              ) : (
                <span className="cwf-def__muted">Not referenced by name from the current graph.</span>
              )}
            </section>
          ) : null}

          <div className="cwf-libcard__identity">
            <span>Component ID</span>
            <code>{c.component_id}</code>
          </div>
        </div>
      ) : null}
    </article>
  );
};

// ─── Shared page chrome ──────────────────────────────────────────────────────

const PageShell: React.FC<{
  icon: string;
  title: string;
  eyebrow?: React.ReactNode;
  intro: React.ReactNode;
  toolbar?: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, title, eyebrow, intro, toolbar, children }) => (
  <div className="cwf-page" data-testid="cwf-page">
    <div className="cwf-page__inner">
      <header className="cwf-page__head">
        <div className="cwf-page__title">
          <span className="cwf-page__badge" aria-hidden="true">
            <i className={`fas ${icon}`} />
          </span>
          <div className="cwf-page__titletext">
            {eyebrow ? <span className="cwf-page__eyebrow">{eyebrow}</span> : null}
            <h2>{title}</h2>
          </div>
        </div>
        <p className="cwf-page__intro">{intro}</p>
        {toolbar ? <div className="cwf-page__toolbar">{toolbar}</div> : null}
      </header>
      {children}
    </div>
  </div>
);

const EmptyState: React.FC<{ icon: string; children: React.ReactNode }> = ({ icon, children }) => (
  <div className="cwf-page__empty">
    <i className={`fas ${icon}`} aria-hidden="true" />
    <p>{children}</p>
  </div>
);

// ─── Library / Triggers / Strategies pages ──────────────────────────────────

export interface WorkflowComponentsPageProps {
  /** Restrict the page to one kind (triggers / strategies) and show per-card usage. Absent — the full library with kind filters. */
  fixedKind?: 'trigger' | 'strategy';
  components: WorkflowComponent[];
  malformed: WorkflowMalformedComponent[];
  wf: EditableWorkflow | null;
  includeArchived: boolean;
  loading: boolean;
  onToggleArchived: (next: boolean) => void;
  onRefresh: () => void;
  onSelectEdge: (edgeId: string) => void;
  onSelectState: (stateId: string) => void;
}

function usagesFor(
  kind: 'trigger' | 'strategy',
  card: WorkflowComponent,
  wf: EditableWorkflow | null,
  onSelectEdge: (id: string) => void,
  onSelectState: (id: string) => void
): CardUsage[] {
  if (!wf) return [];
  if (kind === 'trigger') {
    return wf.edges
      .filter((e) => cardRefOf(e.guard)?.name === card.name)
      .map((e) => ({ label: `${e.source} → ${e.target}`, onClick: () => onSelectEdge(e.id) }));
  }
  return Object.values(wf.states)
    .filter((s) => s.strategies_ref?.name === card.name)
    .map((s) => ({ label: s.state_id, onClick: () => onSelectState(s.state_id) }));
}

const PAGE_INTRO: Record<string, React.ReactNode> = {
  library: 'Every component card the target system publishes for this workflow revision. Archived cards never affect the revision digest.',
  trigger:
    'Exit-condition cards a transition can reference instead of an inline trigger. "Used by" lists the transitions that pin this exact card by name.',
  strategy: 'Strategy cards a state can reference via strategies_ref instead of an inline block. "Used by" lists the states pinning this exact card.',
};

const byNameThenVersion = (a: WorkflowComponent, b: WorkflowComponent) =>
  a.name === b.name ? b.version - a.version : a.name.localeCompare(b.name);

export const WorkflowComponentsPage: React.FC<WorkflowComponentsPageProps> = ({
  fixedKind,
  components,
  malformed,
  wf,
  includeArchived,
  loading,
  onToggleArchived,
  onRefresh,
  onSelectEdge,
  onSelectState,
}) => {
  const [query, setQuery] = useState('');
  const [kindFilter, setKindFilter] = useState('');

  const scoped = useMemo(() => (fixedKind ? components.filter((c) => c.kind === fixedKind) : components), [components, fixedKind]);

  const kinds = useMemo(() => {
    const counts: Record<string, number> = {};
    scoped.forEach((c) => {
      counts[c.kind] = (counts[c.kind] || 0) + 1;
    });
    const known = Object.keys(KIND_META).filter((k) => counts[k]);
    const extra = Object.keys(counts)
      .filter((k) => !KIND_META[k])
      .sort();
    return [...known, ...extra].map((k) => ({ kind: k, count: counts[k] }));
  }, [scoped]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return scoped
      .filter((c) => (kindFilter ? c.kind === kindFilter : true))
      .filter((c) => {
        if (!q) return true;
        const desc = typeof c.definition?.description === 'string' ? c.definition.description : '';
        return `${c.name} ${c.kind} ${c.component_id} ${desc}`.toLowerCase().indexOf(q) !== -1;
      })
      .sort(byNameThenVersion);
  }, [scoped, kindFilter, query]);

  /** The full library mixes six kinds — grouping them beats one alphabetical soup. */
  const groups = useMemo(() => {
    if (fixedKind || kindFilter) return null;
    const order = kinds.map((k) => k.kind);
    return order
      .map((kind) => ({ kind, cards: filtered.filter((c) => c.kind === kind) }))
      .filter((g) => g.cards.length > 0);
  }, [filtered, kinds, fixedKind, kindFilter]);

  const head = fixedKind ? kindMeta(fixedKind) : { title: 'Component library', icon: 'fa-book', color: '' };
  const archivedCount = scoped.filter((c) => c.archived_at).length;

  const cardGrid = (cards: WorkflowComponent[]) => (
    <div className="cwf-lib-grid">
      {cards.map((c) => (
        <ComponentCard
          key={`${c.component_id}:${c.version}`}
          component={c}
          usages={fixedKind ? usagesFor(fixedKind, c, wf, onSelectEdge, onSelectState) : null}
        />
      ))}
    </div>
  );

  return (
    <PageShell
      icon={head.icon}
      title={head.title}
      eyebrow={
        <>
          {scoped.length} card{scoped.length === 1 ? '' : 's'}
          {archivedCount ? ` · ${archivedCount} archived` : ''}
        </>
      }
      intro={PAGE_INTRO[fixedKind || 'library']}
      toolbar={
        <>
          <div className="cwf-page__search">
            <i className="fas fa-search" aria-hidden="true" />
            <input
              className="cwf-input"
              placeholder={`Search ${scoped.length} card${scoped.length === 1 ? '' : 's'}…`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query ? (
              <button type="button" className="cwf-page__search-clear" onClick={() => setQuery('')} aria-label="Clear search">
                ×
              </button>
            ) : null}
          </div>
          <label className="cwf-check cwf-check--sm" title="Archived cards never affect the revision digest.">
            <input type="checkbox" checked={includeArchived} disabled={loading} onChange={(e) => onToggleArchived(e.target.checked)} />
            <span>archived</span>
          </label>
          <button
            type="button"
            className="cwf-btn cwf-btn--sm"
            onClick={onRefresh}
            disabled={loading}
            title="Re-read the component library (include=components)"
          >
            <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`} aria-hidden="true" /> Refresh
          </button>
        </>
      }
    >
      {!fixedKind && kinds.length > 1 ? (
        <div className="cwf-library__kinds">
          <button type="button" className={`cwf-kind ${kindFilter === '' ? 'cwf-kind--active' : ''}`} onClick={() => setKindFilter('')}>
            all <span>{scoped.length}</span>
          </button>
          {kinds.map((k) => (
            <button
              key={k.kind}
              type="button"
              className={`cwf-kind ${kindFilter === k.kind ? 'cwf-kind--active' : ''}`}
              onClick={() => setKindFilter(kindFilter === k.kind ? '' : k.kind)}
              title={k.kind}
              style={{ ['--kind-color' as string]: kindMeta(k.kind).color }}
            >
              <i className={`fas ${kindMeta(k.kind).icon}`} aria-hidden="true" /> {kindMeta(k.kind).title} <span>{k.count}</span>
            </button>
          ))}
        </div>
      ) : null}

      {malformed.length ? (
        <Banner
          tone="warn"
          title={`${malformed.length} malformed card${malformed.length === 1 ? '' : 's'} could not be parsed by the connector`}
          details={malformed.map(
            (m) => [m.kind, m.name, m.version != null ? `v${m.version}` : null].filter(Boolean).join(' · ') + (m.problem ? ` — ${m.problem}` : '')
          )}
        />
      ) : null}

      {scoped.length === 0 ? (
        <EmptyState icon={head.icon}>
          {components.length === 0
            ? 'The snapshot has no component library section — press Refresh to re-read it.'
            : `This workflow's library has no ${fixedKind ? kindMeta(fixedKind).title.toLowerCase() : 'cards'}.`}
        </EmptyState>
      ) : filtered.length === 0 ? (
        <EmptyState icon="fa-search">No cards match the filter.</EmptyState>
      ) : groups ? (
        <div className="cwf-lib-groups">
          {groups.map((g) => (
            <section key={g.kind} className="cwf-lib-group" style={{ ['--card-accent' as string]: kindMeta(g.kind).color }}>
              <header className="cwf-lib-group__head">
                <i className={`fas ${kindMeta(g.kind).icon}`} aria-hidden="true" />
                <h3>{kindMeta(g.kind).title}</h3>
                <span className="cwf-lib-group__count">{g.cards.length}</span>
              </header>
              {cardGrid(g.cards)}
            </section>
          ))}
        </div>
      ) : (
        cardGrid(filtered)
      )}
    </PageShell>
  );
};

// ─── Ontology page ───────────────────────────────────────────────────────────

export interface WorkflowOntologyPageProps {
  ontology: unknown;
  ontologyRevision?: number;
}

function sectionSize(value: unknown): string {
  if (Array.isArray(value)) return `${value.length} ${value.length === 1 ? 'entry' : 'entries'}`;
  if (isPlainObject(value)) {
    const n = Object.keys(value).length;
    return `${n} ${n === 1 ? 'key' : 'keys'}`;
  }
  return '';
}

function ontologyLabel(key: string): string {
  return key.replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function ontologyIcon(key: string): string {
  const normalized = key.toLowerCase();
  if (normalized.includes('knowledge')) return 'fa-book-open';
  if (normalized.includes('layer')) return 'fa-layer-group';
  if (normalized.includes('topic') || normalized.includes('bootstrap')) return 'fa-sitemap';
  if (normalized.includes('relation') || normalized.includes('edge')) return 'fa-project-diagram';
  if (normalized.includes('metric') || normalized.includes('scor')) return 'fa-chart-line';
  if (normalized.includes('status')) return 'fa-circle-check';
  if (normalized.includes('type')) return 'fa-tags';
  return 'fa-shapes';
}

function isOntologyMetadata(key: string): boolean {
  const normalized = key.toLowerCase();
  return ['ontology_revision', 'revision', 'document_version', 'schema_version', 'version'].includes(normalized);
}

function ontologyMetadataValue(value: unknown): string {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Enabled' : 'Disabled';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (Array.isArray(value) && value.every((item) => typeof item === 'string' || typeof item === 'number')) {
    return value.join(', ');
  }
  if (isPlainObject(value)) {
    const entries = Object.entries(value);
    if (entries.length === 1) {
      const [key, item] = entries[0];
      if (item == null || ['string', 'number', 'boolean'].includes(typeof item)) {
        return `${ontologyLabel(key)}: ${ontologyMetadataValue(item)}`;
      }
    }
  }
  return sectionSize(value) || 'Available';
}

type OntologyStats = { groups: number; items: number; values: number; depth: number };

function ontologyStats(value: unknown, depth = 0): OntologyStats {
  if (Array.isArray(value)) {
    return value.reduce<OntologyStats>(
      (total, item) => {
        const nested = ontologyStats(item, depth + 1);
        return {
          groups: total.groups + nested.groups,
          items: total.items + nested.items,
          values: total.values + nested.values,
          depth: Math.max(total.depth, nested.depth),
        };
      },
      { groups: 0, items: value.length, values: 0, depth }
    );
  }
  if (isPlainObject(value)) {
    return Object.values(value).reduce<OntologyStats>(
      (total, item) => {
        const nested = ontologyStats(item, depth + 1);
        return {
          groups: total.groups + nested.groups,
          items: total.items + nested.items,
          values: total.values + nested.values,
          depth: Math.max(total.depth, nested.depth),
        };
      },
      { groups: 1, items: 0, values: 0, depth }
    );
  }
  return { groups: 0, items: 0, values: 1, depth };
}

/** Keeps only matching branches so search remains useful inside large nested sections. */
function filterOntology(value: unknown, query: string): unknown | undefined {
  if (!query) return value;
  if (Array.isArray(value)) {
    const items = value.map((item) => filterOntology(item, query)).filter((item) => item !== undefined);
    return items.length ? items : undefined;
  }
  if (isPlainObject(value)) {
    const matched: Record<string, unknown> = {};
    Object.entries(value).forEach(([key, item]) => {
      if (key.toLowerCase().includes(query)) matched[key] = item;
      else {
        const nested = filterOntology(item, query);
        if (nested !== undefined) matched[key] = nested;
      }
    });
    return Object.keys(matched).length ? matched : undefined;
  }
  return String(value ?? '').toLowerCase().includes(query) ? value : undefined;
}

export const WorkflowOntologyPage: React.FC<WorkflowOntologyPageProps> = ({ ontology, ontologyRevision }) => {
  const sections = useMemo(() => (isPlainObject(ontology) ? Object.entries(ontology) : null), [ontology]);
  const [query, setQuery] = useState('');
  const empty =
    ontology == null ||
    (Array.isArray(ontology) && ontology.length === 0) ||
    (isPlainObject(ontology) && Object.keys(ontology).length === 0);
  const totals = useMemo(() => ontologyStats(ontology), [ontology]);
  const normalizedQuery = query.trim().toLowerCase();
  const metadata = useMemo(() => {
    const items = (sections || [])
      .filter(([key]) => isOntologyMetadata(key))
      .map(([key, value]) => ({ key, value }));
    if (!items.some(({ key }) => key.toLowerCase().includes('ontology_revision'))) {
      items.unshift({ key: 'ontology_revision', value: ontologyRevision });
    }
    return items;
  }, [sections, ontologyRevision]);
  const contentSections = useMemo(
    () => sections?.filter(([key]) => !isOntologyMetadata(key)) || null,
    [sections]
  );
  const visibleSections = useMemo(() => {
    if (!contentSections) return null;
    return contentSections
      .map(([key, value]) => [key, key.toLowerCase().includes(normalizedQuery) ? value : filterOntology(value, normalizedQuery)] as const)
      .filter((entry): entry is readonly [string, unknown] => entry[1] !== undefined);
  }, [contentSections, normalizedQuery]);

  return (
    <PageShell
      icon="fa-shapes"
      title="Ontology"
      eyebrow={
        sections ? (
          <>
            {sections.length} section{sections.length === 1 ? '' : 's'} · read-only
          </>
        ) : (
          'read-only'
        )
      }
      intro={
        <>
          One complete, read-only view of the types, relationships, rules and knowledge published by the target system.
        </>
      }
      toolbar={
        sections ? (
          <div className="cwf-onto-tools">
            <div className="cwf-page__search cwf-onto-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input className="cwf-input" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search names, fields and values…" />
              {query ? <button type="button" className="cwf-page__search-clear" onClick={() => setQuery('')} aria-label="Clear search">×</button> : null}
            </div>
          </div>
        ) : null
      }
    >
      {empty ? (
        <EmptyState icon="fa-shapes">
          The connector returned an empty <code>ontology</code> section for this integration — this page fills in once the target system publishes
          ontology data here.
        </EmptyState>
      ) : sections ? (
        <>
          <section className="cwf-onto-meta" aria-label="Ontology document information">
            <div className="cwf-onto-meta__intro">
              <span className="cwf-onto-meta__icon"><i className="fas fa-file-shield" aria-hidden="true" /></span>
              <span><strong>Document information</strong><small>Current published ontology snapshot</small></span>
            </div>
            <div className="cwf-onto-meta__fields">
              {metadata.map(({ key, value }) => (
                <div key={key} className="cwf-onto-meta__field">
                  <small>{ontologyLabel(key)}</small>
                  <strong>{ontologyMetadataValue(value)}</strong>
                  <code>{key} {ontologyMetadataValue(value)}</code>
                </div>
              ))}
              <span className="cwf-onto-meta__status"><i className="fas fa-circle-check" aria-hidden="true" /> Read-only</span>
            </div>
          </section>

          <div className="cwf-onto-stats">
            <div><i className="fas fa-boxes-stacked" aria-hidden="true" /><span><strong>{contentSections?.length || 0}</strong>Sections</span></div>
            <div><i className="fas fa-list" aria-hidden="true" /><span><strong>{totals.items}</strong>List entries</span></div>
            <div><i className="fas fa-table-cells-large" aria-hidden="true" /><span><strong>{totals.groups}</strong>Data groups</span></div>
            <div><i className="fas fa-layer-group" aria-hidden="true" /><span><strong>{totals.depth}</strong>Max depth</span></div>
          </div>

          <div className="cwf-onto">
            {visibleSections && visibleSections.length ? visibleSections.map(([key, value]) => (
              <section key={key} className="cwf-onto__section cwf-onto__section--open">
                <div className="cwf-onto__head">
                  <span className="cwf-onto__icon" aria-hidden="true"><i className={`fas ${ontologyIcon(key)}`} /></span>
                  <span className="cwf-onto__identity">
                    <strong>{ontologyLabel(key)}</strong>
                    <code>{key}</code>
                  </span>
                  <span className="cwf-onto__count">{sectionSize(value) || 'value'}</span>
                </div>
                <div className="cwf-onto__body">
                  {isPlainObject(value) ? <DefBlocks obj={value} /> : <DefValue value={value} depth={0} />}
                </div>
              </section>
            )) : (
              <div className="cwf-onto-no-results">
                <i className={`fas ${normalizedQuery ? 'fa-search' : 'fa-file-circle-check'}`} aria-hidden="true" />
                <strong>{normalizedQuery ? 'No ontology matches' : 'Document information is up to date'}</strong>
                <span>{normalizedQuery ? 'Try another name, field or value.' : 'This snapshot contains document metadata only.'}</span>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="cwf-onto__section">
          <div className="cwf-onto__body">
            <DefValue value={ontology} depth={0} />
          </div>
        </div>
      )}
    </PageShell>
  );
};
